'use client'

import { Cell, ColorCode, Divider, Image, Spacer, Text } from '@vinted/web-ui'
import { Fragment, useEffect } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import {
  getShipmentItems,
  transformShipmentItemsResponse,
} from '@marketplace-web/complaints/complaints-data'
import { colorCodeToHex } from '@marketplace-web/vendor-abstractions/web-ui-util'

import { truncateString } from '../../../utils/string'

const VISIBLE_THUMBNAILS_LIMIT = 3
const MAX_CHARACTERS = 37

type Props = {
  transactionId?: number
}

const ShipmentItems = ({ transactionId }: Props) => {
  const translate = useTranslate('conversation.actions.request_return_modal')

  const {
    fetch: fetchShipmentItems,
    transformedData: shipmentItems,
    isLoading: isShipmentItemsLoading,
  } = useFetch(getShipmentItems, transformShipmentItemsResponse)

  useEffect(() => {
    if (!transactionId) return

    fetchShipmentItems(transactionId)
  }, [fetchShipmentItems, transactionId])

  const renderOrderTitle = () => {
    if (!shipmentItems) return null

    if (shipmentItems.length > 1) {
      const count = shipmentItems.length.toString()

      return <Text as="h2" type={Text.Type.Title} text={translate('order_count', { count })} />
    }

    return (
      <Text
        as="h2"
        type={Text.Type.Title}
        text={truncateString(shipmentItems[0]!.name, MAX_CHARACTERS)}
      />
    )
  }

  const renderShipmentItem = (item, index) => {
    return (
      <Fragment key={index}>
        <Spacer orientation={Spacer.Orientation.Vertical} />
        <Image
          role="img"
          src={item.imageUrl}
          styling={Image.Styling.Rounded}
          size={Image.Size.Medium}
          scaling={Image.Scaling.Cover}
        />
      </Fragment>
    )
  }

  const renderOrderPhotos = () => {
    if (!shipmentItems) return null

    return (
      <>
        {shipmentItems.length > VISIBLE_THUMBNAILS_LIMIT && (
          <Image
            role="img"
            label={`+${shipmentItems.length - VISIBLE_THUMBNAILS_LIMIT}`}
            styling={Image.Styling.Rounded}
            size={Image.Size.Medium}
            scaling={Image.Scaling.Cover}
            color={colorCodeToHex(ColorCode.GreyscaleLevel4)}
          />
        )}
        {shipmentItems.slice(0, VISIBLE_THUMBNAILS_LIMIT).map(renderShipmentItem)}
      </>
    )
  }

  if (!shipmentItems || shipmentItems.length < 1) return null

  if (isShipmentItemsLoading)
    return <ContentLoader styling={ContentLoader.Styling.Wide} testId="shipment-items-loader" />

  return (
    <>
      <Cell body={renderOrderTitle()} suffix={renderOrderPhotos()} />
      <Divider />
    </>
  )
}

export default ShipmentItems
